// src/components/HeadTags.js
// Injects site-wide verification + analytics tags from the admin-managed SEO
// config. Mounted once near the app root. We render the official snippets from
// simple IDs so the SEO person never pastes raw <script> into the site.
import React, { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import SeoContext from '../context/SeoContext';

// Whitelist the exact shape of each tracking ID before it is interpolated into
// an inline <script>. Anything that doesn't match is dropped, so even a bad
// value already in the DB can never inject script into visitors' pages.
const TRACKING_PATTERNS = {
  gscVerification: /^[A-Za-z0-9_-]+$/,
  bingVerification: /^[A-Za-z0-9_-]+$/,
  ga4Id: /^(G|UA|AW|GT)-[A-Za-z0-9-]+$/,
  gtmId: /^GTM-[A-Za-z0-9]+$/,
  fbPixelId: /^[0-9]+$/,
};
const safeId = (key, value) =>
  typeof value === 'string' && TRACKING_PATTERNS[key].test(value) ? value : '';

const HeadTags = () => {
  const { seo } = useContext(SeoContext);
  const raw = (seo && seo.tracking) || {};
  const t = {
    gscVerification: safeId('gscVerification', raw.gscVerification),
    bingVerification: safeId('bingVerification', raw.bingVerification),
    ga4Id: safeId('ga4Id', raw.ga4Id),
    gtmId: safeId('gtmId', raw.gtmId),
    fbPixelId: safeId('fbPixelId', raw.fbPixelId),
  };

  return (
    <Helmet>
      {t.gscVerification ? (
        <meta name="google-site-verification" content={t.gscVerification} />
      ) : null}
      {t.bingVerification ? (
        <meta name="msvalidate.01" content={t.bingVerification} />
      ) : null}

      {/* Google Analytics 4 */}
      {t.ga4Id ? (
        <script async src={`https://www.googletagmanager.com/gtag/js?id=${t.ga4Id}`}></script>
      ) : null}
      {t.ga4Id ? (
        <script>{`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${t.ga4Id}');
        `}</script>
      ) : null}

      {/* Google Tag Manager */}
      {t.gtmId ? (
        <script>{`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${t.gtmId}');
        `}</script>
      ) : null}

      {/* Meta (Facebook) Pixel */}
      {t.fbPixelId ? (
        <script>{`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window,document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${t.fbPixelId}'); fbq('track', 'PageView');
        `}</script>
      ) : null}
    </Helmet>
  );
};

export default HeadTags;
